// "Bring your phone calls here" — verify phone via SMS code, link this
// device's saved reflections to the phone account, and unlock call history.

import { useState } from "react";
import { Loader2, PhoneCall, ShieldCheck } from "lucide-react";

import { getDeviceId } from "@/lib/device";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhoneLinkCard({
  onLinked,
  title = "Called God on the phone?",
  subtitle = "Every call is kept as a gentle summary. Verify your number and your call history will appear here, joined with what you save in the browser.",
  compact = false,
}) {
  const [step, setStep] = useState("phone"); // phone | code
  const [phone, setPhone] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const startVerify = async (e) => {
    e.preventDefault();
    if (busy || !phone.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/phone/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          res.status === 429
            ? "Too many attempts. Try again in an hour."
            : "Could not send a code to that number.",
        );
        return;
      }
      setNormalizedPhone(data.phone || phone.trim());
      setStep("code");
    } catch {
      setError("Something broke. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const checkCode = async (e) => {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/phone/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: normalizedPhone,
          code: code.trim(),
          device_id: getDeviceId(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(
          res.status === 401
            ? "That code was not right. Check it and try again."
            : "Could not verify right now. Try again.",
        );
        return;
      }
      // Session token is now in an httpOnly cookie (set by the server).
      onLinked(data.phone);
    } catch {
      setError("Something broke. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="phone-link-card"
      className={
        compact
          ? "mt-0 rounded-2xl"
          : "cg-card mt-8 rounded-2xl p-6"
      }
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <PhoneCall className="h-4 w-4" strokeWidth={1.8} />
        <p className="text-[11px] uppercase tracking-[0.2em]">Phone calls</p>
      </div>
      <h2 className="font-serif-display mt-2 text-xl text-foreground">
        {title}
      </h2>
      <p className="mt-1.5 max-w-md text-sm leading-6 text-muted-foreground">
        {subtitle}
      </p>

      {step === "phone" ? (
        <form onSubmit={startVerify} className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input
            type="tel"
            data-testid="phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 (555) 123-4567"
            autoComplete="tel"
            className="w-full rounded-xl border bg-white/70 px-3.5 py-2.5 text-sm text-foreground outline-none sm:max-w-[240px]"
            style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}
          />
          <button
            type="submit"
            data-testid="phone-send-code"
            disabled={busy || !phone.trim()}
            className="cg-btn-primary !min-h-[44px] !px-5 text-sm disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Text me a code"}
          </button>
        </form>
      ) : (
        <form onSubmit={checkCode} className="mt-4">
          <p className="text-[13px] text-muted-foreground">
            We sent a 6-digit code to {normalizedPhone}.
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              inputMode="numeric"
              data-testid="code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              maxLength={8}
              className="w-full rounded-xl border bg-white/70 px-3.5 py-2.5 text-sm tracking-[0.3em] text-foreground outline-none sm:max-w-[160px]"
              style={{ borderColor: "hsla(38, 25%, 70%, 0.8)" }}
            />
            <button
              type="submit"
              data-testid="code-verify"
              disabled={busy || !code.trim()}
              className="cg-btn-primary !min-h-[44px] !px-5 text-sm disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4" strokeWidth={1.8} /> Verify
                </span>
              )}
            </button>
            <button
              type="button"
              data-testid="code-back"
              onClick={() => {
                setStep("phone");
                setCode("");
                setError("");
              }}
              className="cg-btn-ghost !min-h-[44px] !px-4 text-sm"
            >
              Change number
            </button>
          </div>
        </form>
      )}

      {error ? (
        <p
          data-testid="phone-link-error"
          className="mt-2 text-[13px]"
          style={{ color: "hsl(0 60% 42%)" }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
